import React from 'react'
import { Button, CircleLoader } from '@deal/components'
import AirportTypeahead from '#src/components/AirportTypeaheadInput'
import { TextField, TextArea, PhoneField, TextFieldDisplayType } from '@deal/components'
import styles from './styles.css'
import PhoneIcon from './phone.svg'
import omit from 'lodash/omit'
import { InputTextPathFlowQuestionFieldType } from '#src/generated/graphql'
import { InputTextPathFlowQuestionDefintion } from '#src/components/Path/Question'
import { FormContextConsumer, FormContextType } from '#src/components/Path/FormContext'

interface InputAttributes {
  placeholder: string | undefined
  name: string
  defaultValue?: string
  country?: string | null
  valid?: boolean
  errorText?: string
  display?: TextFieldDisplayType
}

interface SendPhoneButtonProps {
  isSubmitting: boolean
  onPageSubmitted: () => void
}

interface CustomProps {
  display?: TextFieldDisplayType
}

interface InputTextQuestionState {
  isValid?: boolean
  errorText?: string
}

const SendPhoneButton: React.SFC<SendPhoneButtonProps> = ({ isSubmitting, onPageSubmitted }) => {
  return (
    <Button disabled={isSubmitting} onSubmit={onPageSubmitted} className={styles.phoneSubmit}>
      {isSubmitting ? (
        <CircleLoader color="#8798a4" />
      ) : (
        <>
          <div className={styles.phoneLogoButton}>
            <PhoneIcon className={styles.icon} />
          </div>
          <div className={styles.phoneTextButton}>SMS</div>
        </>
      )}
    </Button>
  )
}

class InputTextQuestion extends React.PureComponent<
  InputTextPathFlowQuestionDefintion<CustomProps> & { context: FormContextType },
  InputTextQuestionState
> {
  constructor(
    props: InputTextPathFlowQuestionDefintion<CustomProps> & { context: FormContextType }
  ) {
    super(props)

    this.state = {}

    this.handleFieldValueChanged = this.handleFieldValueChanged.bind(this)
  }

  public componentDidMount() {
    const { question } = this.props

    if (question.inputTextUserInput.value) {
      this.props.onFieldValueChanged({
        value: question.inputTextUserInput.value
      })

      this.props.onFieldValidityChanged(true)
    }

    if (!question.required) {
      this.props.onFieldValidityChanged(true)
    }
  }

  public handleIsFieldValid(value: string) {
    const { question } = this.props
    let isFieldValid = true
    let errorMessage = ''

    if (question.required && (value === undefined || value === '')) {
      return {
        isFieldValid: false,
        errorMessage: 'This field is required'
      }
    }

    isFieldValid = question.inputTextValidations.every(validation => {
      if (validation.__typename === 'InputTextPathFlowQuestionLengthValidation') {
        if (validation.minSize && value.length < validation.minSize) {
          return false
        }

        if (validation.maxSize && value.length > validation.maxSize) {
          return false
        }
      } else if (validation.__typename === 'InputTextPathFlowQuestionRegexValidation') {
        if (!new RegExp(validation.regex).test(value)) {
          errorMessage = validation.errorMessage

          return false
        }
      }

      return true
    })

    return {
      isFieldValid,
      errorMessage
    }
  }

  public handleSetFieldInvalid(errorMessage?: string) {
    this.props.onFieldValidityChanged(false)

    this.setState({
      isValid: false,
      errorText: errorMessage || 'Please enter a valid value'
    })
  }

  public handleSetFieldValid() {
    this.props.onFieldValidityChanged(true)

    this.setState({
      isValid: true,
      errorText: undefined
    })
  }

  public handleFieldValueChanged(value: string) {
    const isFieldValid = this.handleIsFieldValid(value)

    if (isFieldValid.isFieldValid) {
      this.handleSetFieldValid()
    } else {
      this.handleSetFieldInvalid(isFieldValid.errorMessage)
    }

    this.props.onFieldValueChanged({ value })
  }

  private deriveInputType(
    fieldType: InputTextPathFlowQuestionFieldType,
    inputAttributes: InputAttributes
  ) {
    const label = this.props.question.internalName

    switch (fieldType) {
      case InputTextPathFlowQuestionFieldType.AIRPORT_TYPEAHEAD:
        return (
          <AirportTypeahead
            label={label}
            labelHidden={true}
            onSetValue={airportId => this.handleFieldValueChanged(airportId)}
            className={styles.airportInput}
            defaultValue={{
              id: inputAttributes.defaultValue ? inputAttributes.defaultValue : '',
              displayName: inputAttributes.defaultValue ? inputAttributes.defaultValue : ''
            }}
            {...omit(inputAttributes, 'defaultValue')}
          />
        )

      case InputTextPathFlowQuestionFieldType.TEXT_AREA:
        return (
          <TextArea
            rows={6}
            onChange={event => this.handleFieldValueChanged(event.target.value)}
            {...omit(inputAttributes, 'errorText')}
          />
        )

      case InputTextPathFlowQuestionFieldType.NUMERIC:
        return (
          <TextField
            autoFocus={false}
            type="tel"
            label={label}
            labelHidden={true}
            onChange={event => this.handleFieldValueChanged(event.target.value)}
            {...inputAttributes}
          />
        )

      case InputTextPathFlowQuestionFieldType.TELEPHONE:
        return (
          <PhoneField
            autoFocus={false}
            testId="phone-input"
            label={label}
            labelHidden={true}
            onChange={event => this.handleFieldValueChanged(event.target.value)}
            {...inputAttributes}
          />
        )

      case InputTextPathFlowQuestionFieldType.TELEPHONE_SUBMIT:
        return (
          <div className={styles.smsContainer}>
            <PhoneField
              autoFocus={false}
              testId="phone-input"
              label={label}
              labelHidden={true}
              onChange={event => this.handleFieldValueChanged(event.target.value)}
              {...inputAttributes}
            />
            <SendPhoneButton
              isSubmitting={this.props.context ? this.props.context.isFormSubmitting : false}
              onPageSubmitted={this.props.context ? this.props.context.onPageSubmitted : () => null}
            />
          </div>
        )

      case InputTextPathFlowQuestionFieldType.EMAIL:
        return (
          <TextField
            autoFocus={false}
            type="email"
            testId="email-input"
            label={label}
            labelHidden={true}
            onChange={event => this.handleFieldValueChanged(event.target.value)}
            {...inputAttributes}
          />
        )

      case InputTextPathFlowQuestionFieldType.HIDDEN:
        return (
          <TextField
            autoFocus={false}
            type="hidden"
            label={label}
            labelHidden={true}
            onChange={event => this.handleFieldValueChanged(event.target.value)}
            {...inputAttributes}
          />
        )

      case InputTextPathFlowQuestionFieldType.INPUT_FIELD:
        return (
          <TextField
            autoFocus={false}
            label={label}
            labelHidden={true}
            onChange={event => this.handleFieldValueChanged(event.target.value)}
            {...inputAttributes}
          />
        )
    }

    throw new Error(`Unhandled InputTextPathFlowQuestion type [${fieldType}]`)
  }

  public render() {
    const { question } = this.props
    let displayType: TextFieldDisplayType | undefined

    if (this.props.display) {
      displayType = this.props.display
    } else {
      displayType = 'standard'
    }

    const inputAttributes: InputAttributes = {
      defaultValue: question.inputTextUserInput.value
        ? question.inputTextUserInput.value
        : undefined,
      placeholder: question.placeholder || undefined,
      name: question.internalName,
      valid: this.state.isValid,
      errorText: this.state.errorText,
      display: displayType
    }

    if (question.fieldType === 'TELEPHONE') {
      const countryCode = question.countryCodeAttribute && question.countryCodeAttribute.values[0]

      if (countryCode && countryCode.__typename === 'AttributeValueEnumeration') {
        inputAttributes.country = countryCode.enumerationValue
      }
    }

    return (
      <div className={styles.input}>
        {this.deriveInputType(question.fieldType, inputAttributes)}
      </div>
    )
  }
}

const InputTextQuestionWithContext: React.SFC<InputTextPathFlowQuestionDefintion<CustomProps>> = ({
  ...rest
}) => (
  <FormContextConsumer>
    {formData => (formData ? <InputTextQuestion context={formData} {...rest} /> : null)}
  </FormContextConsumer>
)

export default InputTextQuestionWithContext
